<template>
  <div class="section has-background-primary full-window">
    <div class="container">
      <GetSurvey v-model="survey" :code-input="codeInput" />

      <div v-if="!hasSurvey">
        <div class="card">
          <div class="card-content has-background-light">
            <p class="title">Code eingeben:</p>
            <div class="content">
              <b-field class="my-4 mx-4">
                <b-input v-model="codeInput" />
              </b-field>
              <b-button type="is-primary" :disabled="codeInput.length != 4" @click="goToSurvey">
                {{ $t("join") }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="hasSurvey && survey.options.personalization && !hasPermission">
        <div class="card">
          <div class="card-content has-background-light">
            <p class="title">Umfrage ist Kennwortgeschützt, geben Sie ihren Code ein:</p>
            <div class="content">
              <b-field class="my-4 mx-4">
                <b-input v-model="personalToken" type="password" />
              </b-field>
              <b-button type="is-primary" :disabled="personalToken.length != 6" @click="tryPersonalToken">
                {{ $t("join") }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="hasSurvey">
        <div class="card">
          <div class="card-content has-background-light">
            <p class="title">
              {{ survey.title }}
            </p>
            <div v-if="hasQuestion">
              <p class="subtitle">
                {{ question.question }}
              </p>
              <div class="content my-4 mx-4">
                <div v-show="!results">
                  <SurveyQuestions :question="question" :can-vote="canVote" @answers="(v) => (answers = v)" />
                </div>
                <div v-if="results && survey.options.showResults">
                  <SurveyResults :question="question" :responses="responses" :server-answer="serverAnswer" />
                </div>
              </div>
              <b-field grouped group-multiline>
                <b-button
                  v-if="!results && canVote"
                  type="is-primary"
                  class="control"
                  :disabled="!canSubmit || isDisabled"
                  :loading="isLoading"
                  @click="submit"
                >
                  {{ $t("submit") }}
                </b-button>
                <b-button
                  v-if="survey.options.showResults && !results"
                  class="control"
                  :loading="isLoading"
                  @click="handleResults(true)"
                >
                  {{ $t("showResults") }}
                </b-button>
                <b-button
                  v-if="results && canVote"
                  type="is-primary"
                  class="control"
                  :disabled="isDisabled"
                  :loading="isLoading"
                  @click="handleResults(false)"
                >
                  {{ $t("vote") }}
                </b-button>
                <b-button class="control" @click="goBackToCodeInput">
                  {{ $t("goBackToCodeInput") }}
                </b-button>
              </b-field>
              <div v-if="hasQuestion && survey.options.timed">
                <b-progress
                  class="mt-4"
                  :type="questionProgress < 50 ? 'is-success' : questionProgress < 80 ? 'is-warning' : 'is-danger'"
                  :value="questionProgress"
                  show-value
                >
                  <span v-if="questionProgress < 100 || !question.closed" class="has-text-black">
                    {{
                      Math.floor(
                        survey.options.duration -
                          Math.abs(question.open_time.toDate().getTime() - new Date().getTime()) / 1000,
                      )
                    }}s
                  </span>
                  <span v-else class="has-text-black">
                    {{ $t("closed") }}
                  </span>
                </b-progress>
              </div>
            </div>
            <div v-else>
              <div class="content">
                <p class="subtitle">Keine Frage ausgewählt</p>
                <b-button @click="goBackToCodeInput">
                  {{ $t("goBackToCodeInput") }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="subtitle">Keine Frage ausgewählt</p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @todo write security code feature
 */
import GetSurvey from "@/components/survey/GetSurvey";
import SurveyQuestions from "@/components/survey/SurveyQuestions";
import SurveyResults from "@/components/survey/SurveyResults";
import { v4 } from "uuid";
import { db, functions } from "@/firebase";

export default {
  name: "SurveyParticipate",
  components: {
    GetSurvey,
    SurveyQuestions,
    SurveyResults,
  },
  data() {
    return {
      codeInput: "", // survey code, 4 letters
      personalToken: "", // optinal, for personal surveys, 6 letters
      survey: {}, // firestore doc
      answers: [], // answer array
      serverAnswer: {}, // firestore doc of what current user has voted
      interval: null, // JS interval to check duration of question
      results: false, // Boolean, if results are currently shown or if the question is currently shown
      responses: null, // firestore doc with all the results
      loadedQuestion: null, // index like currentQuestion, to check if question has changed
      questionProgress: null, // percentage between 0-100, to track duration of timed questions
      isDisabled: false,
      isLoading: false,
      hasPermission: false, // if the personal token was correct
      surveyToken: "",
    };
  },
  computed: {
    canVote() {
      if (this.survey && this.question && !this.question.closed) {
        if (!this.survey.options.timed || this.questionProgress < 100) {
          return true;
        }
      }
      return false;
    },
    canSubmit() {
      if (this.answers && this.answers.filter((y) => y == true).length > 0) {
        return true;
      }
      return false;
    },
    question() {
      if (this.hasSurvey) {
        if (this.survey.currentQuestion != null) {
          return this.survey.questions[this.survey.currentQuestion];
        }
      }
      clearInterval(this.interval);
      return {};
    },
    hasSurvey() {
      return this.survey != null && Object.keys(this.survey).length != 0;
    },
    hasQuestion() {
      return this.question != null && Object.keys(this.question).length != 0;
    },
  },
  watch: {
    survey() {
      if (this.hasSurvey) {
        if (this.survey.options.showResults) {
          if (this.loadedQuestion != this.survey.currentQuestion) {
            this.resetForNextQuestion();
            this.startTimer();
            this.loadAnswers();
            this.loadPreviousAnswer();
            this.loadedQuestion = this.survey.currentQuestion;
          } else if (this.survey.options.timed && !this.question.closed) {
            this.startTimer();
          }
        }
      }
    },
  },
  mounted() {
    this.surveyToken = v4();
    //localStorage.setItem('surveyToken', v4())
    //if (localStorage.getItem('surveyToken') == null) {
    //}
  },
  methods: {
    goToSurvey() {
      this.$router.push({
        name: "Polling",
        params: { surveyCode: this.codeInput },
      });
    },
    goBackToCodeInput() {
      this.reset();
    },
    // check if user can switch view to results & begin loading answers
    async handleResults(value) {
      if (this.survey.options.showResults) {
        this.results = value;
      } else {
        this.results = false;
      }
    },
    async loadAnswers() {
      this.$bind(
        "responses",
        db.collection("surveys").doc(this.survey.id).collection("answers").doc(this.survey.currentQuestion.toString()),
      );
    },
    async loadPreviousAnswer() {
      this.$bind(
        "serverAnswer",
        db
          .collection("surveys")
          .doc(this.survey.id)
          .collection("answers")
          .doc(this.survey.currentQuestion.toString())
          .collection("votes")
          .doc(this.surveyToken),
      );
    },
    startTimer() {
      this.questionProgress = 0;
      this.interval = setInterval(this.checkProgress, 700);
    },
    checkProgress() {
      if (this.survey.options.timed) {
        if (this.question) {
          this.questionProgress =
            (Math.abs(this.question.open_time.toDate().getTime() - new Date().getTime()) /
              1000 /
              this.survey.options.duration) *
            100;
          console.log(this.questionProgress);
          this.isDisabled = false;
          if (this.questionProgress >= 100) {
            this.questionProgress = 100;
            this.isDisabled = true;
            clearInterval(this.interval);
            this.handleResults(true);
          }
          if (this.question.closed) {
            this.questionProgress = 100;
            this.isDisabled = true;
            clearInterval(this.interval);
            this.handleResults(true);
          }
          return;
        }
      }
      clearInterval(this.interval);
    },
    async submit() {
      try {
        this.isDisabled = true;
        this.isLoading = true;
        await functions.httpsCallable("AnswerSurvey")({
          surveyId: this.survey.id,
          questionId: this.survey.currentQuestion.toString(),
          answer: this.answers,
          surveyToken: this.surveyToken,
          timestamp: new Date(),
          personalToken: this.personalToken,
        });
        this.$buefy.toast.open({
          type: "is-success",
          message: this.$t("voteAccepted"),
        });
        this.handleResults(true);
      } catch (error) {
        this.$buefy.toast.open({
          type: "is-warning",
          message: this.$t(error),
        });
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },
    reset() {
      this.codeInput = "";
      this.personalToken = "";
      this.survey = {};
      this.answers = [];
      this.serverAnswer = {};
      clearInterval(this.interval);
      this.interval = null;
      this.results = false;
      this.responses = null;
      this.loadedQuestion = null;
      this.questionProgress = null;
      this.isDisabled = false;
      this.isLoading = false;
      this.hasPermission = false;
    },
    resetForNextQuestion() {
      this.isLoading = false;
      this.isDisabled = false;
      this.answers = [];
      this.serverAnswer = {};
      this.questionProgress = 0;
      clearInterval(this.interval);
    },
    async tryPersonalToken() {
      this.isLoading = true;
      this.isDisabled = true;
      try {
        let filteredPersons = this.survey.persons.filter((y) => y.personalToken == this.personalToken);

        if (filteredPersons.length > 0) {
          this.hasPermission = true;
        } else {
          throw false;
        }
      } catch (error) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: this.$t("surveyPermissionNotFound"),
        });
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },
  },
};
</script>

<style>
.full-window {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #121c2e;
  z-index: 2000;
}
</style>
